/**
 * UserContextMenu Component
 * Notes+ - Bosph Technologies
 * 
 * WhatsApp-style context menu for user names/avatars in chat messages.
 * Handles:
 * - Left click: Open/start private chat
 * - Right click: Show context menu (Add friend / Send private message)
 */

import React, { useState } from 'react';
import { MessageCircle, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContextMenu from './ContextMenu';
import { chatApi } from '../../services/cloudApi';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

/**
 * UserContextMenu - Reusable component for user name/avatar interactions
 * @param {Object} props
 * @param {Object} props.user - User object (required)
 * @param {Object} props.children - React node to wrap (user name/avatar)
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Disable interactions
 * @param {string} props.wrapper - Wrapper element type: 'span' | 'div' (default: 'span')
 */
const UserContextMenu = ({ 
  user, 
  children, 
  className = '',
  disabled = false,
  wrapper = 'span'
}) => {
  const { t } = useTranslation('shared');
  const { authToken, user: currentUser } = useAuth();
  const { friends, selectFriend } = useChat();
  const [contextMenu, setContextMenu] = useState({ isOpen: false, x: 0, y: 0 });

  if (!user || disabled) {
    return <span className={className}>{children}</span>;
  }

  // Check if user is current user
  const isCurrentUser = user.id === currentUser?.id || user.email === currentUser?.email;

  // Check if user is a friend
  const isFriend = friends?.some(
    (f) => !f.isDeleted && (f.email || f.id) === (user.email || user.id)
  );

  // Handle left click - Open/start private chat
  // Even if not a friend, open chat screen (backend will handle message sending restrictions)
  const handleLeftClick = (e) => {
    if (isCurrentUser || disabled) return;
    
    e.preventDefault();
    e.stopPropagation();

    const friendId = user.email || user.id;
    if (friendId && selectFriend) {
      // selectFriend will open chat screen even if not a friend
      // Backend will enforce message sending restrictions
      selectFriend(friendId);
    }
  };

  // Handle right click - Show context menu
  const handleRightClick = (e) => {
    if (isCurrentUser || disabled) {
      // Don't show context menu for current user
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    setContextMenu({
      isOpen: true,
      x: e.clientX,
      y: e.clientY,
    });
  };

  // Context menu items
  const contextMenuItems = [];

  if (!isCurrentUser) {
    if (isFriend) {
      contextMenuItems.push({
        label: t('userContextMenu.sendPrivateMessage'),
        icon: MessageCircle,
        onClick: () => {
          const friendId = user.email || user.id;
          if (friendId && selectFriend) {
            selectFriend(friendId);
          }
        },
      });
    } else if (user.email) {
      contextMenuItems.push({
        label: t('userContextMenu.addFriend'),
        icon: UserPlus,
        onClick: async () => {
          try {
            await chatApi.sendFriendRequest({
              token: authToken,
              receiverEmail: user.email,
            });
            window.dispatchEvent(
              new CustomEvent('notesplus-show-toast', {
                detail: {
                  type: 'success',
                  title: t('userContextMenu.friendRequestTitle'),
                  message: t('userContextMenu.friendRequestSent', { email: user.email }),
                },
              })
            );
          } catch (err) {
            console.error('Send friend request error from UserContextMenu:', err);
            const message = getApiErrorMessage(err, { fallback: t('userContextMenu.friendRequestError') });
            window.dispatchEvent(
              new CustomEvent('notesplus-show-toast', {
                detail: {
                  type: 'error',
                  title: t('userContextMenu.errorTitle'),
                  message,
                },
              })
            );
          }
        },
      });
    }
  }

  const WrapperComponent = wrapper === 'div' ? 'div' : 'span';
  const isDivWrapper = wrapper === 'div';

  return (
    <>
      <WrapperComponent
        className={`${className} ${!isCurrentUser && !disabled ? 'cursor-pointer' : ''} ${!isDivWrapper && !isCurrentUser && !disabled ? 'hover:underline' : ''}`}
        onClick={handleLeftClick}
        onContextMenu={handleRightClick}
        title={!isCurrentUser && !disabled ? t('userContextMenu.tooltip') : undefined}
        style={!isCurrentUser && !disabled && isDivWrapper ? { cursor: 'pointer' } : undefined}
      >
        {children}
      </WrapperComponent>

      {/* Context Menu */}
      <ContextMenu
        isOpen={contextMenu.isOpen && contextMenuItems.length > 0}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        items={contextMenuItems}
        onClose={() => setContextMenu({ isOpen: false, x: 0, y: 0 })}
      />
    </>
  );
};

export default UserContextMenu;
